import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Lock, Edit2, Trash2, BookOpen, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getCollection, updateCollection, removeFromCollection } from '../services/api';
import { ComicCard } from '../components/ComicCard';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

function CollectionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ name: '', description: '', is_public: true });

  useEffect(() => {
    fetchCollection();
  }, [id]);

  const fetchCollection = async () => {
    try {
      const res = await getCollection(id);
      setCollection(res.data);
      setEditData({
        name: res.data.name,
        description: res.data.description,
        is_public: res.data.is_public
      });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('This collection is private');
      } else {
        toast.error('Collection not found');
      }
      navigate('/collections');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await updateCollection(id, editData);
      toast.success('Collection updated');
      setEditing(false);
      fetchCollection();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleRemoveComic = async (comicId) => {
    try {
      await removeFromCollection(id, comicId);
      toast.success('Comic removed');
      setCollection({
        ...collection,
        comics: collection.comics.filter(c => c.id !== comicId),
        comic_count: collection.comic_count - 1
      });
    } catch (error) {
      toast.error('Failed to remove comic');
    }
  };

  const isOwner = user && collection && user.id === collection.user_id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!collection) return null;

  return (
    <div className="min-h-screen pb-24 pt-8 page-padding" data-testid="collection-detail-page">
      <SEO title={collection.name} description={collection.description || `A collection by ${collection.username}`} />
      
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Link to="/collections" className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-white mb-6">
          <ArrowLeft size={20} />
          Back to Collections
        </Link>

        {/* Header */}
        <div className="card-hotrod p-6 mb-8">
          {editing ? (
            <div className="space-y-4">
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                className="w-full px-4 py-3 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)] text-xl font-teko"
              />
              <textarea
                value={editData.description}
                onChange={(e) => setEditData({...editData, description: e.target.value})}
                rows={2}
                className="w-full px-4 py-3 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)] resize-none"
              />
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="edit_public"
                  checked={editData.is_public}
                  onChange={(e) => setEditData({...editData, is_public: e.target.checked})}
                />
                <label htmlFor="edit_public" className="text-sm">Public collection</label>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setEditing(false)} className="btn-outline">Cancel</button>
                <button onClick={handleUpdate} className="btn-hotrod">Save</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    {collection.is_public ? (
                      <Globe size={20} className="text-green-500" />
                    ) : (
                      <Lock size={20} className="text-yellow-500" />
                    )}
                    <h1 className="text-3xl md:text-4xl font-teko">{collection.name}</h1>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] mb-2">
                    Created by <span className="text-[var(--primary)]">{collection.username}</span>
                  </p>
                  {collection.description && (
                    <p className="text-[var(--text-secondary)]">{collection.description}</p>
                  )}
                </div>
                
                {isOwner && (
                  <button
                    onClick={() => setEditing(true)}
                    className="p-2 hover:bg-[var(--background)] rounded-lg transition-colors"
                  >
                    <Edit2 size={20} />
                  </button>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-[var(--border)]">
                <span className="text-sm text-[var(--text-secondary)]">
                  {collection.comic_count} {collection.comic_count === 1 ? 'comic' : 'comics'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Comics Grid */}
        {collection.comics && collection.comics.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {collection.comics.map((comic) => (
              <div key={comic.id} className="relative group">
                <ComicCard comic={comic} />
                {isOwner && (
                  <button
                    onClick={() => handleRemoveComic(comic.id)}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove from collection"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="card-hotrod p-12 text-center">
            <BookOpen size={48} className="mx-auto mb-4 text-[var(--text-secondary)] opacity-50" />
            <p className="text-[var(--text-secondary)] mb-4">This collection is empty</p>
            {isOwner && (
              <Link to="/browse" className="btn-outline inline-flex items-center gap-2">
                <Plus size={16} />
                Add Comics
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CollectionDetailPage;
